import type { DriftAccount, DriftQueueFilter } from '@interfaces/driftQueue';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { Typography as MuiTypography } from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import type { DataTableTanstackTable } from '@ovComponents/2-component/dataTable';
import { DataTable } from '@ovComponents/2-component/dataTable';
import { useThemeTokens } from '@providers/themeTokenProvider';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DriftQueueEmptyState } from './driftQueueEmptyState';
import { DriftQueueFooter } from './driftQueueFooter';
import { DriftQueueStatCards } from './driftQueueStatCards';
import { formatLastRefreshed } from './formatLastRefreshed';
import type { UseDriftQueueCounts } from './useDriftQueue';

/** Scroll cap for the table body — header, stat cards, and footer stay fixed. */
const TABLE_MAX_HEIGHT = '32vh';

export type DriftQueueVisualProps = {
  table: DataTableTanstackTable<DriftAccount>;
  counts: UseDriftQueueCounts;
  activeFilter: DriftQueueFilter;
  onFilterChange: (next: DriftQueueFilter) => void;
  isLoading: boolean;
  error: Error | null;
  totalRowCount: number;
  filteredRowCount: number;
  onRowClick: (account: DriftAccount) => void;
  /**
   * Override the page title (defaults to the `driftQueue:title` i18n key).
   * Pass a string to replace the heading entirely, e.g. for embedded use.
   */
  title?: string;
  /**
   * Timestamp of the last data refresh shown below the title.
   * Defaults to `new Date()` at render time when not provided.
   */
  lastRefreshedAt?: Date;
  /**
   * Handler for the "View All" header button.
   * When `undefined` the button is hidden — never renders a dead control.
   */
  onViewAllClick?: () => void;
  /** When false, hides the "As of …" line under the widget title (e.g. v2 dashboard). */
  showLastRefreshed?: boolean;
};

export const DriftQueueVisual: FC<DriftQueueVisualProps> = ({
  table,
  counts,
  activeFilter,
  onFilterChange,
  isLoading,
  error,
  totalRowCount,
  filteredRowCount,
  onRowClick,
  title,
  lastRefreshedAt,
  onViewAllClick,
  showLastRefreshed = true,
}) => {
  const { t } = useTranslation(['driftQueue']);
  const { sys } = useThemeTokens();
  const { pageSize } = table.getState().pagination;

  const resolvedTitle = title ?? t('driftQueue:title');

  const refreshedLabel = t('driftQueue:lastRefresh.label', {
    value: formatLastRefreshed(lastRefreshedAt ?? new Date()),
  });

  const tableSection = (
    <DataTable<DriftAccount>
      table={table}
      rowsPerPage={pageSize}
      loading={isLoading}
      tableProps={{
        size: 'small',
        sx: { overflow: 'visible' },
      }}
      emptyContent={
        <DriftQueueEmptyState
          filterHasNoMatches={filteredRowCount === 0 && totalRowCount > 0}
        />
      }
      tablePagination={
        filteredRowCount > 50
          ? {
              mode: 'tanstack',
              showPerPageSelect: true,
              pageSizes: [25, 50, 100],
            }
          : { mode: 'tanstack', showPerPageSelect: false, pageSizes: [25] }
      }
      getRowProps={(row) => ({
        hover: true,
        sx: { cursor: 'pointer' },
        onClick: () => onRowClick(row.original),
      })}
    />
  );

  return (
    <Box
      data-testid='drift-queue'
      sx={{
        p: 1.5,
        '& .MuiTableHead-root .MuiTableCell-root': {
          paddingTop: '8px',
          paddingBottom: '8px',
          whiteSpace: 'nowrap',
        },
        '& .MuiTableBody-root .MuiTableCell-root': {
          paddingTop: '4px',
          paddingBottom: '4px',
        },
      }}
    >
      {/* Page header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
          pb: 1,
          borderBottom: `1px solid ${sys.color.outline}`,
        }}
      >
        {/* Left cluster: title (+ optional last-refresh on standalone pages) */}
        <Box>
          <Typography variant='headingSmall'>{resolvedTitle}</Typography>
          {showLastRefreshed && (
            <Typography variant='bodySmall' colorVariant='variant'>
              {refreshedLabel}
            </Typography>
          )}
        </Box>

        {/* Right cluster: red alert indicator + View All */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {counts.needsAction > 0 && (
            <Box
              role='status'
              aria-label={String(
                t('driftQueue:header.queueAlertA11yLabel', {
                  count: counts.needsAction,
                }),
              )}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: 10,
                backgroundColor: sys.color.negative,
                color: sys.color.onNegative,
              }}
            >
              <ReportProblemIcon sx={{ fontSize: 16 }} />
              <Typography
                variant='labelSmall'
                sx={{ color: sys.color.onNegative, fontWeight: 700 }}
              >
                {t('driftQueue:header.queueAlertLabel', {
                  count: counts.needsAction,
                })}
              </Typography>
            </Box>
          )}

          {onViewAllClick && (
            <Button
              variant='outlined'
              size='sm'
              label={t('driftQueue:header.viewAll')}
              onClick={onViewAllClick}
            />
          )}
        </Box>
      </Box>

      {/* Error banner */}
      {error !== null && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1,
            borderRadius: 1,
            backgroundColor: sys.color.negativeVariant,
          }}
        >
          <MuiTypography color='error'>{error.message}</MuiTypography>
        </Box>
      )}

      {/* Solid-color filter stat cards */}
      <DriftQueueStatCards
        activeFilter={activeFilter}
        onFilterChange={onFilterChange}
        counts={counts}
      />

      {/* Data table — only the data rows scroll. The cap + overflow live on the
          MUI `TableContainer` so it is the actual scroll context (otherwise its
          default `overflow-x: auto` makes it hijack sticky positioning and the
          column headers / footer scroll away). The column headers stay pinned to
          the top (sticky thead) and the pagination footer stays pinned to the
          bottom (sticky footer), while the widget header + filter cards above
          never scroll. */}
      <Box
        data-testid='drift-queue-table-scroll'
        sx={{
          '& .MuiTableContainer-root': {
            maxHeight: TABLE_MAX_HEIGHT,
            overflowY: 'auto',
          },
          '& .MuiTableHead-root .MuiTableCell-root': {
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: sys.color.surface,
          },
          // The pagination footer is rendered immediately after the table;
          // pin it to the bottom so per-page / page-count controls stay
          // visible while the rows scroll.
          '& .MuiTable-root + *': {
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            backgroundColor: sys.color.surface,
          },
        }}
      >
        {tableSection}
      </Box>

      <DriftQueueFooter shown={filteredRowCount} total={totalRowCount} />
    </Box>
  );
};
